/* tslint:disable:no-console
 *
 * Any data that has to be persisted across ApiCaller invocations should reside
 * here. Although this uses a redux store, it shouldn't be combined in anyway
 * with the main store we use for the app. This is the reason the store, and
 * reducers are not exported. The exported facet is a thin wrapper that uses
 * the store and actions.
 *
 * The reason for having a pluggable store structure is to split out state
 * handling from the actual `fetch` wrapper implementations. This way we can
 * have, for instance, separate handling for fdp state tracking and completely
 * different storage and state tracking for cross DC switching.
 *
 * Some notices:
 *
 * 1. Most state-driven code tends to act buggy when that state is hoisted to a
 *    global param. And in the case of ApiCaller which is reused across chunks
 *    non-trivial usecases, it's even harder to reason about.
 *
 * 2. If you must, make it as your intent as clear as possible. Don't add
 *    random variables to existing context store unless they strictly belong
 *    there. DO NOT expose the store directly.
 *
 * 3. Try keeping the final exported function interface simple. Avoid adding
 *    too many functions to the exports. Keep that interface as light-weight as
 *    possible.
 */
import { TERMINAL_ID_COOKIE } from 'constants/common';
import { getCookie } from 'utils/CookieUtils';
import { DC, SupportedHostname } from './types';
import fdpHelper from 'utils/FDPHelper';
import metricsTracker from 'utils/MetricsTracker';
import { createStore } from 'redux';
import get from 'lodash/get';
const AvailableConfig = {
    'www.flipkart.com': {
        primary: 'www.flipkart.com',
        dcSpecific: {
            [DC.CH]: 'www.flipkart.com',
            [DC.HYD]: 'www.flipkart.com'
        }
    },
    'rome.api.flipkart.net': {
        primary: 'rome.api.flipkart.net',
        dcSpecific: {
            [DC.CH]: '1.rome.api.flipkart.net',
            [DC.HYD]: '2.rome.api.flipkart.net'
        }
    },
    'rome.api.flipkart.com': {
        primary: 'rome.api.flipkart.com',
        dcSpecific: {
            [DC.CH]: '1.rome.api.flipkart.com',
            [DC.HYD]: '2.rome.api.flipkart.com'
        }
    },
    [__API_HOST__]: {
        primary: __API_HOST__,
        dcSpecific: {
            [DC.CH]: __API_HOST_DC_1__ || __API_HOST__,
            [DC.HYD]: __API_HOST_DC_2__ || __API_HOST__
        }
    }
};
export const EnabledConfig = __API_HOST_ENABLED__ || SupportedHostname.WWW;
// Arbitrarily decided number of key-value pairs of cookie -> hostname mappings
// to retain before all are cleared.
// TODO: Measure this in production setting somehow
const HOSTNAME_MAP_THRESHOLD = 5000;
// Don't make fdp calls for _every_ possible api call success, and do it
// when this number of subsequent APIs succeed.
const ACK_THROTTLE_COUNT = 3;
const UNSET_T_COOKIE = 'unknownTCookie';
// getCookie handles two cases: where it picks from document object if
// ExecutionEnvironment.canUseDOM works, or picks from the cookie string if
// passed explicitly. So this acts a common approach for both the client and
// server. However, on server calls, care needs to be taken to pass the
// serialized cookie, and not an object
function terminalId(context) {
    return getCookie(TERMINAL_ID_COOKIE, context.headers['Cookie']) || UNSET_T_COOKIE;
}
var ContextAction;
(function (ContextAction) {
    ContextAction[ContextAction["RESET"] = 1] = "RESET";
    ContextAction[ContextAction["UPDATE_DOMAIN_SCHEME"] = 2] = "UPDATE_DOMAIN_SCHEME";
    ContextAction[ContextAction["SET_HOSTNAME"] = 3] = "SET_HOSTNAME";
    ContextAction[ContextAction["LOGGED_IN_FDP"] = 4] = "LOGGED_IN_FDP";
    ContextAction[ContextAction["SET_CONFIG_FLAGS"] = 5] = "SET_CONFIG_FLAGS";
})(ContextAction || (ContextAction = {}));
const InitialState = {
    configFlags: {},
    logCount: 0,
    currentConfigKey: EnabledConfig,
    userHostnameMap: new Map()
};
if (__DEBUG_ENV__) {
    console.log('ContextStore: AvailableConfig: ', AvailableConfig);
    console.log('ContextStore: EnabledConfig: ', EnabledConfig);
    console.log('ContextStore: InitialState: ', InitialState);
}
const validUpdateDomainAction = (action) => {
    return action && 'to' in action && Boolean(AvailableConfig[action.to]);
};
const validSetHostnameAction = (action) => {
    return action && 'cookie' in action && 'dcID' in action;
};
const contextReducer = (state = InitialState, action) => {
    switch (action.type) {
        case ContextAction.RESET:
            return Object.assign({}, InitialState, { userHostnameMap: new Map() });
        case ContextAction.UPDATE_DOMAIN_SCHEME:
            // Note: A hard-to-debug bug happened when the userHostnameMap
            // wasn't getting cleaned up after a domain scheme change. Example
            // usecase is when we disable helios, all the calls irrespective of
            // what information the server had stored should go to
            // www.flipkart.com, but since the hostnamemap gets changed in the
            // `hostnameForDcIDRedux` function, that hostname was always
            // getting set to the one that was stored for that user.
            // But only do this if there actually is a change in the base hostname.
            if (!validUpdateDomainAction(action) || state.currentConfigKey === action.to) {
                return state;
            }
            return Object.assign(Object.assign({}, state), { currentConfigKey: action.to, userHostnameMap: new Map(), logCount: 0 });
        case ContextAction.SET_HOSTNAME:
            if (validSetHostnameAction(action)) {
                const { cookie, dcID } = action;
                // TODO: Build a better primitive for this. This check is to
                // avoid the size of this map growing without bounds, which
                // could happen depending on the amount of traffic and the
                // number of processes handling web requests, when combined
                // with how long the servers are kept running.
                if (state.userHostnameMap.size >= HOSTNAME_MAP_THRESHOLD) {
                    state.userHostnameMap.clear();
                }
                // tslint:disable-next-line: no-use-before-declare
                state.userHostnameMap.set(cookie, hostnameForDcIDRedux(dcID));
            }
            return state;
        case ContextAction.LOGGED_IN_FDP:
            let logCount = state.logCount;
            if (logCount < ACK_THROTTLE_COUNT) {
                logCount++;
            }
            else {
                logCount = 0;
            }
            return Object.assign(Object.assign({}, state), { logCount });
        case ContextAction.SET_CONFIG_FLAGS:
            return Object.assign(Object.assign({}, state), { configFlags: action.configFlags });
        default:
            return state;
    }
};
// NOTE: DO NOT export this and use it somewhere directly.
const stateStore = createStore(contextReducer);
const hostnameForDcIDRedux = (dcID) => {
    // TODO: add better logging here if for some reason there's no
    // currentConfigKey in the hostname map. This happened when the stage
    // deployment config was faulty.
    const currentConfigKey = stateStore.getState().currentConfigKey;
    const hostnameCfg = AvailableConfig[currentConfigKey];
    if (typeof dcID !== 'undefined' && hostnameCfg.dcSpecific[dcID]) {
        return hostnameCfg.dcSpecific[dcID];
    }
    else {
        return hostnameCfg.primary;
    }
};
export const store = {
    logInFdp(requestContext) {
        const { response, context } = requestContext;
        const shouldLogInFdp = (response) => {
            const isNotUnderThrottle = stateStore.getState().logCount <= ACK_THROTTLE_COUNT;
            if (response.ok && isNotUnderThrottle) {
                const responseHeaders = response.headers;
                const isAckResponse = responseHeaders.get('X-ACK-RESPONSE');
                const requestId = responseHeaders.get('X-Request-ID');
                if (__DEBUG_ENV__) {
                    console.log('reading ack headers: ', isAckResponse, requestId);
                }
                return isAckResponse && requestId;
            }
            return false;
        };
        if (shouldLogInFdp(response)) {
            const responseHeaders = response.headers;
            const requestHeaders = context.headers;
            const requestId = responseHeaders.get('X-Request-ID');
            fdpHelper.setHeaders(requestHeaders);
            fdpHelper.trackEvent({
                rid: requestId,
                en: 'RA'
            });
            metricsTracker.setCount('action.FDP_RA');
            stateStore.dispatch({
                type: ContextAction.LOGGED_IN_FDP
            });
        }
    },
    // logs abV2 information received in api callresponse into FDP
    logABv2InfoInFdp(response) {
        // track `ABE` fdp event only when config flag is enabled
        if (stateStore.getState().configFlags.enableABV2Changes) {
            const abWrapper = get(response.META_INFO, 'abContext.abWrapper');
            if (abWrapper && abWrapper.length > 0) {
                fdpHelper.trackEvent({
                    en: 'ABE',
                    abi: abWrapper
                });
            }
        }
    },
    setHostname(context, dcID) {
        const cookie = terminalId(context);
        if (!cookie) {
            if (__DEBUG_ENV__) {
                console.warn('setHostname: Expected cookie to be present');
            }
            return;
        }
        stateStore.dispatch({
            type: ContextAction.SET_HOSTNAME,
            cookie,
            dcID
        });
    },
    getHostname(context) {
        // TODO: add fallbacks if T cookie is not set
        const cookie = terminalId(context);
        const defaultHostname = hostnameForDcIDRedux();
        // explicit `as` typecasting because we don't care if the value is null
        // or undefined. But we can't default to an empty string because that
        // has a different behaviour as it's a valid key in a Map
        const hostnameForCookie = stateStore.getState().userHostnameMap.get(cookie);
        if (cookie && hostnameForCookie) {
            return hostnameForCookie;
        }
        return defaultHostname;
    },
    reset() {
        stateStore.dispatch({
            type: ContextAction.RESET
        });
    },
    updateConfigFlags(configFlags) {
        stateStore.dispatch({
            type: ContextAction.SET_CONFIG_FLAGS,
            configFlags
        });
    },
    updateDomainScheme(to) {
        stateStore.dispatch({
            type: ContextAction.UPDATE_DOMAIN_SCHEME,
            to
        });
    }
};
